import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { ArrowDownCircle, ArrowUpCircle, AlertTriangle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { DialplanNodeData } from '@/types/dialplan-nodes';

const ICON_MAP: Record<string, LucideIcon> = {
  inbound_route: ArrowDownCircle,
  outbound_route: ArrowUpCircle,
  emergency_route: AlertTriangle
};

export const RoutingNode: React.FC<NodeProps<DialplanNodeData>> = ({ data, selected, type }) => {
  const IconComponent = ICON_MAP[type || 'inbound_route'] || ArrowDownCircle;
  const isEmergency = type === 'emergency_route';
  
  return (
    <div
      className={`
        px-4 py-3 rounded-lg border-2 bg-gradient-to-br
        ${isEmergency 
          ? 'from-red-50 to-red-100 border-red-300' 
          : 'from-blue-50 to-blue-100 border-blue-300'}
        ${selected ? (isEmergency ? 'border-red-500 shadow-lg' : 'border-blue-500 shadow-lg') : 'shadow'}
        min-w-[180px]
      `}
    >
      <Handle
        type="target"
        position={Position.Left}
        className={`${isEmergency ? '!bg-red-500' : '!bg-blue-500'} !w-3 !h-3`}
      />

      <div className="flex items-center gap-2 mb-2">
        <IconComponent className={`h-5 w-5 ${isEmergency ? 'text-red-600' : 'text-blue-600'}`} />
        <div className="flex-1">
          <div className={`font-semibold ${isEmergency ? 'text-red-900' : 'text-blue-900'}`}>
            {data.label}
          </div>
          {data.description && (
            <div className={`text-xs ${isEmergency ? 'text-red-700' : 'text-blue-700'}`}>
              {data.description}
            </div>
          )}
        </div>
        {!data.enabled && (
          <div className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
            Disabled
          </div>
        )}
      </div>

      {data.dialplanConfig?.pattern && (
        <div className={`text-xs ${isEmergency ? 'text-red-700' : 'text-blue-700'} mt-1`}>
          Pattern: {data.dialplanConfig.pattern}
        </div>
      )}

      {data.priority !== undefined && (
        <div className={`text-xs ${isEmergency ? 'text-red-700' : 'text-blue-700'} mt-1`}>
          Priority: {data.priority}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Right}
        className={`${isEmergency ? '!bg-red-500' : '!bg-blue-500'} !w-3 !h-3`}
      />
    </div>
  );
};

